<!-- src/view/AuthInfoList.vue -->
<template>
  <div class="auth-info-list">
    <div class="page-header">
      <h2>辅助认证信息管理</h2>
      <a-button type="primary" @click="handleAdd">
        <a-icon type="plus" />
        添加手机号
      </a-button>
    </div>

    <!-- 数据表格 -->
    <a-table
      :columns="columns"
      :data-source="tableData"
      :pagination="pagination"
      :loading="loading"
      :bordered="true"
      size="small"
      row-key="id"
      style="margin-top: 16px;"
      @change="handleTableChange"
    >
      <template slot="actions" slot-scope="text, record">
        <a-button type="link" @click="handleEdit(record)">编辑</a-button>
      </template>
    </a-table>

    <!-- 添加手机号弹窗 -->
    <a-modal
      title="添加手机号"
      :visible="modalVisible"
      :confirm-loading="submitting"
      @ok="handleSubmitPhoneNumber"
      @cancel="handleCancelModal"
    >
      <a-form-item label="手机号" :required="true">
        <a-input
          v-model="phoneNumberInput"
          placeholder="请输入手机号"
          :max-length="11"
        />
      </a-form-item>
    </a-modal>

    <!-- 抽屉组件 -->
    <AuthInfoDrawer
      ref="authInfoDrawer"
      :visible="drawerVisible"
      :selectPhoneNumber="selectPhoneNumber"
      :drawer-title="drawerTitle"
      @close="handleDrawerClose"
      @upload-success="handleUploadSuccess"
      @upload-error="handleUploadError"
    />
  </div>
</template>

<script>
/**
 * 辅助认证信息列表页面
 * 包含表格和添加按钮
 */
import { Button, Icon, Table, Modal, Input, Form, message } from 'ant-design-vue'
import AuthInfoDrawer from '@/components/AuthInfoDrawer.vue'
import { getFileData, addPhoneNumber } from '@/api/faceRecognition'

export default {
  name: 'AuthInfoList',
  components: {
    'a-button': Button,
    'a-icon': Icon,
    'a-table': Table,
    'a-modal': Modal,
    'a-input': Input,
    'a-form-item': Form.Item,
    AuthInfoDrawer
  },
  data() {
    return {
      loading: false,
      drawerVisible: false,
      drawerTitle: '添加辅助认证信息',
      modalVisible: false,
      submitting: false,
      phoneNumberInput: '',
      tableData: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条记录`,
        showSizeChanger: true,
        showQuickJumper: true
      },
      searchKeyword: '',
      columns: [
        {
          title: '#',
          dataIndex: 'id',
          width: 60,
          align: 'center'
        },
        {
          title: '手机号',
          dataIndex: 'phoneNumber',
          key: 'phoneNumber'
        },
        {
          title: '操作',
          key: 'actions',
          align: 'center',
          width: 150,
          scopedSlots: { customRender: 'actions' }
        }
      ],
      selectPhoneNumber: ''
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    /**
     * 加载表格数据
     */
    async loadData() {
      this.loading = true
      try {
        const res = await getFileData(
          this.pagination.current,
          this.pagination.pageSize,
          this.searchKeyword
        )
        console.log(res)
        // 根据实际返回的数据结构调整
        if (res.data.data && Array.isArray(res.data.data.folders)) {
          this.tableData = res.data.data.folders.map((item, index) => ({
            id: index + 1,
            phoneNumber: item.phoneNumber || '--',
            fileNameList: item.fileNameList || []
          }))
          this.pagination.total = res.data.data.pagination.total
        } else {
          this.tableData = []
          this.pagination.total = 0
        }
      } catch (err) {
        console.error('加载数据失败:', err)
        message.error('加载数据失败，请稍后重试')
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    /**
     * 添加按钮点击事件
     */
    handleAdd() {
      this.phoneNumberInput = ''
      this.modalVisible = true
    },
    /**
     * 提交手机号
     */
    async handleSubmitPhoneNumber() {
      // 空值校验
      if (!this.phoneNumberInput || !this.phoneNumberInput.trim()) {
        message.warning('请输入手机号')
        return
      }

      this.submitting = true
      try {
       const res = await addPhoneNumber(this.phoneNumberInput.trim())
        message.success(res.data.message)
        this.modalVisible = false
        this.phoneNumberInput = ''
        // 刷新列表，重置到第一页
        this.pagination.current = 1
        await this.loadData()
      } catch (err) {
        console.error('添加手机号失败:', err)
        message.error(err.response?.data?.message || '添加手机号失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    /**
     * 取消弹窗
     */
    handleCancelModal() {
      this.modalVisible = false
      this.phoneNumberInput = ''
    },
    /**
     * 关闭抽屉
     */
    handleDrawerClose() {
      this.drawerVisible = false
    },
    /**
     * 上传成功回调
     */
    handleUploadSuccess(actionKey, data) {
      console.log('上传成功:', actionKey, data)
      // 可以在这里刷新表格数据
      // this.loadData()
    },
    /**
     * 上传失败回调
     */
    handleUploadError(actionKey, error) {
      console.error('上传失败:', actionKey, error)
    },
    /**
     * 查看记录
     */
    handleEdit(record) {
      this.$refs.authInfoDrawer.fileData = record
      this.drawerVisible = true
    },
    /**
     * 表格变化处理（分页、排序、筛选等）
     * @param {object} pagination - 分页信息
     */
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    }
  }
}
</script>

<style scoped>
.auth-info-list {
  padding: 24px;
  background: #fff;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
}
</style>

